import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { Link } from "wouter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Calendar, Filter, ShoppingCart, X } from "lucide-react";
import { type ShowWithDetails, type ArtistWithShows } from "@shared/schema";
import { PlanStorage } from "@/lib/plan-storage";
import { type AdvancedFilters, AdvancedFilterContent } from "@/components/advanced-filter";
import { UnifiedFilterBar } from "@/components/unified-filter-bar";
import {
  applyHardFilters,
  applyAudienceFiltersToArtists,
  getDefaultAdvancedFilters,
  type ArtistWithMatch
} from "@/lib/audience-matching";
import { getMatchBadgeClasses } from '@/lib/match-colors';
import Navbar from "@/components/navbar";
import ArtistCard from "@/components/artist-card";
import ArtistModal from "@/components/artist-modal";
import { useToast } from "@/hooks/use-toast";
import { todayKey } from "@shared/date-utils";
import { apiRequest } from "@/lib/queryClient";
import { filterByDmaIds, filterByArtistName, filterByVenueName } from "@shared/filter-engine";

export default function BrowseShows() {
  const { user } = useAuth();
  const { activePersona } = usePersona();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedArtist, setSelectedArtist] = useState<ArtistWithShows | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedShows, setSelectedShows] = useState<ShowWithDetails[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(getDefaultAdvancedFilters());
  const [formFilters, setFormFilters] = useState<AdvancedFilters>(getDefaultAdvancedFilters());


  // Load selected shows from localStorage on mount
  useEffect(() => {
    const storedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(storedShows);
  }, [user?.id]);

  // Parse URL parameters on mount to set initial filters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const location = urlParams.get("location") || "";
    const genre = urlParams.get("genre") || "";
    const dateFrom = urlParams.get("dateFrom") || "";
    const dateTo = urlParams.get("dateTo") || "";
    const venueTypeParam = urlParams.get("venueType") || "";

    if (location || genre || dateFrom || dateTo || venueTypeParam) {
      const initialFilters = {
        ...getDefaultAdvancedFilters(),
        location,
        genre: genre ? [genre] : [],
        venueType: venueTypeParam ? venueTypeParam.split(',') : [],
        dateFrom,
        dateTo
      };
      setAdvancedFilters(initialFilters);
      setFormFilters(initialFilters);
    }
  }, []);

  // Apply active persona filters
  useEffect(() => {
    if (activePersona) {
      const personaFilters: AdvancedFilters = {
        ...formFilters,
        targetAudience: {
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        },
        // NOTE: targetLocations are now converted to DMA IDs for hard filtering
        // They no longer contribute to location string or percentage match
      };
      setAdvancedFilters(personaFilters);
      setFormFilters(personaFilters);
    } else {
      // Clear audience filters when persona is removed
      const clearedFilters = {
        ...formFilters,
        targetAudience: {
          fanGender: [],
          fanEthnicity: [],
          fanAge: [],
          fanIncome: [],
          fanAttributes: [],
        },
      };
      setAdvancedFilters(clearedFilters);
      setFormFilters(clearedFilters);
    }
  }, [activePersona]);

  // Always show sticky bar when there are selected shows (no scroll handler needed)

  const { data: artists = [], isLoading } = useQuery<ArtistWithShows[]>({
    queryKey: ["/api/artists-with-shows"],
    enabled: true,
  });

  // Apply filters with useMemo to prevent unnecessary recalculations
  const filteredArtists = useMemo(() => {
    if (!artists.length) return [];

    // STEP 1: Apply DMA hard filter first (if persona has targetDmaIds)
    let filtered = artists;
    if (activePersona?.targetDmaIds && activePersona.targetDmaIds.length > 0) {
      filtered = filterByDmaIds(artists, activePersona.targetDmaIds);
    }

    // STEP 2: Apply artist name and venue name filters
    if (advancedFilters.artistName) {
      filtered = filterByArtistName(filtered, advancedFilters.artistName);
    }
    if (advancedFilters.venueName) {
      filtered = filterByVenueName(filtered, advancedFilters.venueName);
    }

    // STEP 3: Check if active persona has audience filters
    const hasPersonaFilters = activePersona && Object.values({
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    }).some(arr => arr.length > 0);

    // STEP 4: If persona filters are active, use persona data for audience matching
    if (hasPersonaFilters) {
      const personaFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        },
        minMatchPercentage: activePersona.minMatchPercentage || 0,
      };

      return applyAudienceFiltersToArtists(filtered, personaFilters);
    }

    // If no persona filters, apply existing advanced filters (for backward compatibility)
    return applyAudienceFiltersToArtists(filtered, advancedFilters);
  }, [artists, advancedFilters, activePersona]);

  // Calculate average match percentage when persona is active
  const averageMatchPercentage = useMemo(() => {
    if (!activePersona || filteredArtists.length === 0) return undefined;
    
    const totalMatch = filteredArtists.reduce((sum, artist) => 
      sum + (artist.audienceMatchPercentage || 0), 0
    );
    
    return Math.round(totalMatch / filteredArtists.length);
  }, [activePersona, filteredArtists]);

  const handleFormFiltersChange = (filters: AdvancedFilters) => {
    setFormFilters(filters);
  };

  const handleApplyFilters = () => {
    setAdvancedFilters(formFilters);
    
    // Update URL with hard filters
    const params = new URLSearchParams();
    if (formFilters.location) params.set("location", formFilters.location);
    if (formFilters.genre && formFilters.genre.length > 0) params.set("genre", formFilters.genre[0]);
    if (formFilters.venueType && formFilters.venueType.length > 0) params.set("venueType", formFilters.venueType.join(','));
    if (formFilters.dateFrom) params.set("dateFrom", formFilters.dateFrom);
    if (formFilters.dateTo) params.set("dateTo", formFilters.dateTo);
    
    const queryString = params.toString();
    window.history.pushState({}, "", `/browse-shows${queryString ? `?${queryString}` : ""}`);
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultAdvancedFilters();
    setFormFilters(defaultFilters);
    setAdvancedFilters(defaultFilters);
    
    // Clear URL parameters
    window.history.pushState({}, "", `/browse-shows`);
  };

  const handleViewArtist = (artist: ArtistWithShows) => {
    setSelectedArtist(artist);
    setShowModal(true);
  };

  const handleAddToProposal = (show: ShowWithDetails) => {
    const wasAdded = PlanStorage.addShow(show, user?.id);
    if (wasAdded) {
      const updatedShows = PlanStorage.getSelectedShows(user?.id);
      setSelectedShows(updatedShows);
      toast({
        title: "Show added to proposal",
        description: `${show.artist.name} at ${show.venue.name} has been added to your proposal.`,
      });
    } else {
      toast({
        title: "Show already in proposal",
        description: `${show.artist.name} at ${show.venue.name} is already in your proposal.`,
        variant: "destructive",
      });
    }
  };

  const handleBulkAddToProposal = (shows: ShowWithDetails[]) => {
    let addedCount = 0;
    let duplicateCount = 0;
    
    shows.forEach(show => {
      const wasAdded = PlanStorage.addShow(show, user?.id);
      if (wasAdded) {
        addedCount++;
      } else {
        duplicateCount++;
      }
    });
    
    const updatedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(updatedShows);
    
    if (addedCount > 0 && duplicateCount === 0) {
      toast({
        title: "Shows added to proposal",
        description: `${addedCount} show${addedCount === 1 ? '' : 's'} added to your proposal.`,
      });
    } else if (addedCount > 0 && duplicateCount > 0) {
      toast({
        title: "Shows processed",
        description: `${addedCount} show${addedCount === 1 ? '' : 's'} added, ${duplicateCount} already in proposal.`,
      });
    } else {
      toast({
        title: "No shows added",
        description: "All selected shows are already in your proposal.",
        variant: "destructive",
      });
    }
    
    // Trigger a page refresh or state update to reflect the change in the sticky bar
    window.dispatchEvent(new Event('proposal-storage-changed'));
  };

  // State for advanced filters dialog
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Handler for unified filter bar changes
  const handleUnifiedFilterChange = useCallback((key: string, value: string | string[]) => {
    // Genre comes as a string from the unified filter bar but is stored as string[]
    if (key === 'genre') {
      const stringValue = typeof value === 'string' ? value : value[0] || '';
      handleFormFiltersChange({
        ...formFilters,
        genre: stringValue ? [stringValue] : [],
      });
    } else if (Array.isArray(value)) {
      // Array filters (venueType, careerStage) - pass through as-is
      handleFormFiltersChange({
        ...formFilters,
        [key]: value,
      });
    } else {
      handleFormFiltersChange({
        ...formFilters,
        [key]: value,
      });
    }
  }, [formFilters, handleFormFiltersChange]);

  // Remove authentication check - allow basic search for all users

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      {/* Unified Filter Bar */}
      <UnifiedFilterBar
        currentMethod="shows"
        filters={{
          location: formFilters.location,
          genre: formFilters.genre && formFilters.genre.length > 0 ? formFilters.genre[0] : '',
          dateFrom: formFilters.dateFrom,
          dateTo: formFilters.dateTo,
          artistName: formFilters.artistName,
          venueName: formFilters.venueName,
          careerStage: formFilters.careerStage,
          venueType: formFilters.venueType,
        }}
        onFilterChange={handleUnifiedFilterChange}
        onClearFilters={handleClearFilters}
        onAdvancedFilters={() => setShowAdvancedFilters(true)}
        isAuthenticated={!!user}
      />

      {/* Advanced Filters Dialog */}
      {showAdvancedFilters && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowAdvancedFilters(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Advanced Filters</h2>
              <button
                onClick={() => setShowAdvancedFilters(false)}
                className="rounded-full opacity-80 ring-offset-background transition-all hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-background/90 backdrop-blur-sm border shadow-lg p-1.5 hover:shadow-xl"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>
            <AdvancedFilterContent
              key={JSON.stringify(advancedFilters)}
              currentFilters={advancedFilters}
              onFiltersChange={(filters) => {
                handleFormFiltersChange(filters);
                setShowAdvancedFilters(false);
              }}
              browsePage="artists"
            />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900">Browse Shows</h1>
          <p className="text-neutral-600 mt-2">
            Find the perfect sponsorship opportunities for your brand
          </p>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-neutral-600">
            {isLoading ? "Loading..." : `${filteredArtists.length} artist${filteredArtists.length !== 1 ? 's' : ''} and ${filteredArtists.reduce((total, artist) => total + (artist.upcomingShows?.length || 0), 0)} show${filteredArtists.reduce((total, artist) => total + (artist.upcomingShows?.length || 0), 0) !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="h-48 bg-neutral-200 rounded mb-4"></div>
                  <div className="h-4 bg-neutral-200 rounded mb-2"></div>
                  <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-neutral-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredArtists.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">No artists found</h3>
            <p className="text-neutral-600">Try adjusting your search filters to find more artists.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArtists.map((artist) => (
              <div key={artist.id} className="relative">
                <ArtistCard
                  artist={artist}
                  onViewArtist={handleViewArtist}
                  isAuthenticated={!!user}
                />
                {/* Show audience match percentage if filtering is active */}
                {Object.values(advancedFilters.targetAudience).some(arr => arr.length > 0) && (
                  <div className="absolute top-2 right-2">
                    <Badge className={getMatchBadgeClasses(Math.round(artist.audienceMatchPercentage || 0))}>
                      {Math.round(artist.audienceMatchPercentage || 0)}% match
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ArtistModal
        artist={selectedArtist}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAddToProposal={handleAddToProposal}
        onBulkAddToProposal={handleBulkAddToProposal}
        isAuthenticated={!!user}
      />
    </div>
  );
}
